import { defineStore } from "pinia";
import * as refundContracts from "@nectarpay/api-contracts/refund";
import {
  RefundStore,
  Refund,
  CreateRefundPayload,
  RefundWithDebitPayload,
  ZeroDollarReturnPayload,
  RefundPaymentPayload,
} from "@/payments/types/refund";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import axios from "@/app/plugins/axios";
import type {
  ExecuteRefundRequest,
  ExecuteRefundResponse,
  RefundPreviewRequest,
  RefundPreviewResponse,
} from "@nectarpay/api-contracts/refund";

export const useRefundStore = defineStore("refund", {
  state: (): RefundStore => ({
    refunds: [],
    selectedRefund: null,
    loading: false,
  }),
  getters: {
    isLoading: (state) => state.loading,
    getRefunds: (state) => state.refunds,
    getSelectedRefund: (state) => state.selectedRefund,
  },
  actions: {
    setSelectedLocation(refund: Refund | null): void {
      this.selectedRefund = refund;
    },
    async createRefund(payload: CreateRefundPayload): Promise<Refund | undefined> {
      try {
        const parsedPayload = refundContracts.createRefundRequestSchema.parse(payload);
        const response = await axios.post<Refund>("/refund", parsedPayload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      }
    },
    async refundWithDebit(
      payload: RefundWithDebitPayload,
      paymentIntentId: string,
      terminalId: string,
    ): Promise<Refund | undefined> {
      try {
        const parsedParams = refundContracts.refundDebitParamsSchema.parse({
          paymentIntentId,
          terminalId,
        });
        const parsedPayload = refundContracts.refundWithDebitRequestSchema.parse(payload);
        const response = await axios.post<Refund>(
          `/refund/debit/${parsedParams.paymentIntentId}/${parsedParams.terminalId}`,
          parsedPayload,
        );
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
      }
    },
    async fetchRefunds(): Promise<void> {
      try {
        this.loading = true;
        const response = await axios.get<{ data: Refund[] }>("/refund");
        this.refunds = response.data.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
        this.loading = false;
      } finally {
        this.loading = false;
      }
    },
    async retrieveRefund(refundId: string): Promise<Refund | undefined> {
      try {
        this.loading = true;
        const parsedParams = refundContracts.refundIdParamsSchema.parse({ refundId });
        const response = await axios.get<Refund>(`/refund/${parsedParams.refundId}`);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
        this.loading = false;
      } finally {
        this.loading = false;
      }
    },
    async zeroDollarReturn(
      paymentIntentId: string,
      data: ZeroDollarReturnPayload,
    ): Promise<Refund> {
      const response = await axios.post<Refund>(`/refund/zero-dollar/${paymentIntentId}`, data);
      return response.data;
    },

    async previewRefund(
      paymentIntentId: string,
      request: RefundPreviewRequest,
    ): Promise<RefundPreviewResponse> {
      const parsedRequest = refundContracts.refundPreviewRequestSchema.parse(request);
      const response = await axios.post<RefundPreviewResponse>(
        `/refund/${paymentIntentId}/preview`,
        parsedRequest,
      );
      return refundContracts.refundPreviewResponseSchema.parse(response.data);
    },

    async executeRefund(
      paymentIntentId: string,
      request: ExecuteRefundRequest,
      idempotencyKey: string,
    ): Promise<ExecuteRefundResponse> {
      const parsedRequest = refundContracts.executeRefundRequestSchema.parse(request);
      const response = await axios.post<ExecuteRefundResponse>(
        `/refund/${paymentIntentId}/execute`,
        parsedRequest,
        { headers: { "Idempotency-Key": idempotencyKey } },
      );
      return refundContracts.executeRefundResponseSchema.parse(response.data);
    },

    async refundPayment(
      paymentIntentId: string,
      data: RefundPaymentPayload,
    ): Promise<Refund | undefined> {
      const { amount, metadata, returnItems, reason } = data;

      const payload = {
        amount,
        metadata,
        payment_intent: paymentIntentId,
        reason,
        returnItems,
      };

      try {
        this.loading = true;
        const parsedPayload = refundContracts.createRefundRequestSchema.parse(payload);
        const response = await axios.post<Refund>(`/refund`, parsedPayload);
        return response.data;
      } catch (error) {
        useSnackBarStore().toastError((error as { data?: { message?: string } }).data);
        this.loading = false;
        throw error;
      } finally {
        this.loading = false;
      }
    },
  },
});
